import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const RELAY = 'wss://relay.bridgelin.online';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'turn:184.94.212.159:3478?transport=tcp', username: 'bridgelink', credential: 'Br1dg3L1nk@2026' },
  { urls: 'turns:184.94.212.159:5349', username: 'bridgelink', credential: 'Br1dg3L1nk@2026' }
];

const css = `
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideIn { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
  .bl-pulse { animation: pulse 2s ease-in-out infinite; }
  .bl-spin { animation: spin 1s linear infinite; display:inline-block; }
  .bl-fade { animation: fadeIn 0.2s ease; }
  .bl-slide { animation: slideIn 0.15s ease; }
  .bl-card:hover { border-color: #7c6aff !important; background: #13131c !important; transform: translateY(-1px); box-shadow: 0 4px 20px rgba(124,106,255,0.12); }
  .bl-card { transition: all 0.18s ease; }
  .bl-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }
  .bl-btn { transition: all 0.15s ease; }
  .bl-icon-btn:hover { background: #252535 !important; border-color: #555 !important; }
  .bl-icon-btn { transition: all 0.15s ease; }
  .bl-danger-btn:hover { background: #3a0d0d !important; border-color: #ff4444 !important; color: #ff4444 !important; }
  .bl-danger-btn { transition: all 0.15s ease; }
  input:focus { border-color: #7c6aff !important; box-shadow: 0 0 0 2px rgba(124,106,255,0.15) !important; }
  input { transition: border-color 0.15s, box-shadow 0.15s; }
  .bl-file-entry:hover { background: #16161f !important; }
  .bl-file-entry { transition: background 0.1s; }
  .bl-monitor-opt:hover { background: #1a1a2a !important; }
  .bl-monitor-opt { transition: background 0.1s; }
  * { box-sizing: border-box; }
`;

function AgentLocation({ ip }) {
  const [loc, setLoc] = useState('');
  useEffect(() => {
    if (!ip) return;
    const cleanIp = ip.replace('::ffff:', '');
    if (cleanIp.startsWith('192.168') || cleanIp.startsWith('10.') || cleanIp === '127.0.0.1') { setLoc('📍 Local Network'); return; }
    fetch(`https://ip-api.com/json/${cleanIp}?fields=status,country,regionName,city`)
      .then(r => r.json())
      .then(d => { if (d.status === 'success') setLoc(`📍 ${d.city}, ${d.regionName}, ${d.country}`); })
      .catch(() => {});
  }, [ip]);
  return loc ? <div style={{ color: '#ffaa00', fontSize: 11, marginTop: 3 }}>{loc}</div> : null;
}

export default function App() {
  const [screen, setScreen] = useState('login');
  const [passphrase, setPassphrase] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentPings, setAgentPings] = useState({});
  const [activeAgent, setActiveAgent] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [lastKey, setLastKey] = useState('');
  const [privacyOn, setPrivacyOn] = useState(false);
  const [screenStatus, setScreenStatus] = useState('on');
  const [screenAlert, setScreenAlert] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [agentLocation, setAgentLocation] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorType, setCursorType] = useState('default');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [fileTransferStatus, setFileTransferStatus] = useState('');
  const [fileTransferDone, setFileTransferDone] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePath, setBrowsePath] = useState('');
  const [browseEntries, setBrowseEntries] = useState([]);
  const [browseParent, setBrowseParent] = useState(null);
  const [browseError, setBrowseError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [agentOffline, setAgentOffline] = useState(false);
  const [reconnectCountdown, setReconnectCountdown] = useState(0);
  const [streamError, setStreamError] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatMsg, setChatMsg] = useState('');
  const [chatSent, setChatSent] = useState('');
  const [clipboardStatus, setClipboardStatus] = useState('');
  const [monitors, setMonitors] = useState([]);
  const [activeMonitor, setActiveMonitor] = useState(1);
  const [showMonitors, setShowMonitors] = useState(false);
  const [mouseEnabled, setMouseEnabled] = useState(false);
  const mouseEnabledRef = useRef(false);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('Connecting...');
  const [latency, setLatency] = useState(null);
  const [quality, setQuality] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const latencyIntervalRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [licenseChecking, setLicenseChecking] = useState(false);
  const [licenseValid, setLicenseValid] = useState(false);
  const [uninstallToast, setUninstallToast] = useState('');
  const [userActive, setUserActive] = useState(false);
  const [keylogBuffer, setKeylogBuffer] = useState([]);
  const userActiveTimerRef = useRef(null);
  const keylogTimerRef = useRef(null);
  const [toolboxScripts, setToolboxScripts] = useState([]);
  const [showToolbox, setShowToolbox] = useState(null); // agentDeviceId
  const [toolboxAgent, setToolboxAgent] = useState(null);

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const videoRef = useRef(null);
  const activeAgentRef = useRef(null);
  const controlRef = useRef(null);
  const keyTimeoutRef = useRef(null);
  const alertTimeoutRef = useRef(null);
  const tokenRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const lastMouseMove = useRef(0);
  const lastClickTime = useRef(0);
  const lastClickPos = useRef({ x: 0, y: 0 });
  const fileChunksRef = useRef({});
  const pcGenRef = useRef(0);
  const browseReqIdRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const agentOfflineRef = useRef(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    document.title = 'BridgeLink';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>⬡</text></svg>';
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => { activeAgentRef.current = activeAgent; }, [activeAgent]);

  useEffect(() => {
    const saved = localStorage.getItem('bl_license');
    if (saved) {
      fetch('https://relay.bridgelin.online/license/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: saved })
      }).then(r => r.json()).then(d => {
        if (d.valid) setLicenseValid(true);
        else { localStorage.removeItem('bl_license'); setLicenseValid(false); }
      }).catch(() => { setLicenseValid(true); });
    }
  }, []);

  useEffect(() => {
    if (screen === 'control' && controlRef.current) controlRef.current.focus();
    if (screen === 'control') {
      setSessionTime(0);
      sessionTimerRef.current = setInterval(() => setSessionTime(t => t + 1), 1000);
    } else {
      clearInterval(sessionTimerRef.current);
      setSessionTime(0);
    }
    return () => clearInterval(sessionTimerRef.current);
  }, [screen]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) controlRef.current?.requestFullscreen();
    else document.exitFullscreen();
  };

  const fetchAgentLocation = async (agent) => {
    if (!agent?.ip) return;
    const cleanIp = agent.ip.replace('::ffff:', '');
    if (cleanIp.startsWith('192.168') || cleanIp.startsWith('10.') || cleanIp === '127.0.0.1') { setAgentLocation('📍 Local Network'); return; }
    try {
      const res = await fetch(`https://ip-api.com/json/${cleanIp}?fields=status,country,regionName,city`);
      const data = await res.json();
      if (data.status === 'success') setAgentLocation(`📍 ${data.city}, ${data.regionName}, ${data.country}`);
    } catch { setAgentLocation(null); }
  };

  const refreshAgents = () => {
    if (!socketRef.current?.connected) { setRefreshing(true); setTimeout(() => setRefreshing(false), 1000); return; }
    setRefreshing(true);
    socketRef.current.emit('controller:get-agents');
    setTimeout(() => setRefreshing(false), 1200);
  };

  const setTransferStatus = (msg, done = false) => { setFileTransferStatus(msg); setFileTransferDone(done); };

  const browseDir = (path) => {
    setBrowseLoading(true); setBrowseError('');
    const reqId = ++browseReqIdRef.current;
    sendCommand({ type: 'fs:list', path, requestId: reqId });
  };

  const openBrowser = () => { setBrowsing(true); browseDir('~'); };

  const scheduleReconnect = (agent, delay = 8) => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setReconnectCountdown(delay);
    countdownTimerRef.current = setInterval(() => {
      setReconnectCountdown(prev => { if (prev <= 1) { clearInterval(countdownTimerRef.current); return 0; } return prev - 1; });
    }, 1000);
    reconnectTimerRef.current = setTimeout(() => {
      if (agentOfflineRef.current) return;
      setStreamError('');
      startWebRTC(agent);
    }, delay * 1000);
  };

  const validateLicense = async () => {
    if (!licenseKey.trim()) { setLicenseError('Please enter your license key'); return; }
    setLicenseChecking(true); setLicenseError('');
    try {
      const res = await fetch('https://relay.bridgelin.online/license/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: licenseKey.trim().toUpperCase() })
      });
      const data = await res.json();
      if (data.valid) { localStorage.setItem('bl_license', licenseKey.trim().toUpperCase()); setLicenseValid(true); setLicenseError(''); }
      else { setLicenseError(data.reason || 'Invalid license key'); }
    } catch { setLicenseError('Connection failed. Try again.'); }
    setLicenseChecking(false);
  };

  const login = () => {
    setConnecting(true); setError('');
    const socket = io(RELAY, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => socket.emit('controller:auth', { passphrase }));
    socket.on('auth:success', (data) => { setConnecting(false); setScreen('dashboard'); setError(''); if (data?.token) tokenRef.current = data.token; });
    socket.on('auth:failed', (data) => { setConnecting(false); setError(data?.reason || 'Wrong passphrase'); socket.disconnect(); });
    socket.on('session:expired', () => { alert('Session expired.'); tokenRef.current = null; setScreen('login'); socket.disconnect(); });
    socket.on('connect_error', () => { setConnecting(false); setError('Connection failed. Try again.'); socket.disconnect(); });
    socket.on('disconnect', () => { if (screen === 'control') setStreamError('Relay disconnected — reconnecting...'); });

    socket.on('agent:list', (list) => {
      setAgents(list);
      // Fetch toolbox scripts
      fetch(RELAY.replace('wss://','https://') + '/admin/toolbox', { headers: { 'x-admin-token': tokenRef.current || '' } })
        .then(r => r.json()).then(d => setToolboxScripts(d.scripts || [])).catch(() => {});
      list.forEach(agent => {
        const start = Date.now();
        socketRef.current.emit('controller:ping-agent', { deviceId: agent.deviceId });
        setTimeout(() => { setAgentPings(prev => ({ ...prev, [agent.deviceId]: Date.now() - start })); }, 500);
      });
      const agent = activeAgentRef.current;
      if (agent && agentOfflineRef.current) {
        const found = list.find(a => a.deviceId === agent.deviceId);
        if (found) { agentOfflineRef.current = false; setAgentOffline(false); setStreamError(''); setStatus('Agent back online — reconnecting...'); setTimeout(() => startWebRTC(found), 1000); }
      }
    });

    socket.on('agent:disconnected', (data) => {
      const agent = activeAgentRef.current;
      if (agent && data.deviceId === agent.deviceId) {
        agentOfflineRef.current = true; setAgentOffline(true); setStatus('Agent offline'); setStreamError('Agent went offline');
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        setReconnectCountdown(0);
      }
    });

    socket.on('webrtc:answer', async (data) => {
      const pc = pcRef.current;
      if (!pc || pc.signalingState !== 'have-local-offer') return;
      try { await pc.setRemoteDescription(new RTCSessionDescription(data.answer)); } catch (e) {}
    });

    socket.on('webrtc:ice', async (data) => {
      const pc = pcRef.current;
      if (!pc || !pc.remoteDescription || !data.candidate) return;
      try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch (e) {}
    });

    const reattachVideo = () => {
      if (videoRef.current && pcRef.current) {
        const receivers = pcRef.current.getReceivers();
        if (receivers.length > 0) {
          const stream = new MediaStream(receivers.map(r => r.track).filter(Boolean));
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }
    };

    socket.on('screen:status', (data) => { setScreenStatus(data.status); if (data.status === 'on') { setPrivacyOn(false); reattachVideo(); } if (data.status === 'off') setPrivacyOn(true); });
    socket.on('screen:restored', () => { setScreenStatus('on'); setPrivacyOn(false); setScreenAlert(true); if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current); alertTimeoutRef.current = setTimeout(() => setScreenAlert(false), 8000); reattachVideo(); });

    socket.on('user:active', () => {
      setUserActive(true);
      if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
      userActiveTimerRef.current = setTimeout(() => setUserActive(false), 3000);
    });

    socket.on('user:keylog', (data) => {
      setUserActive(true);
      if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
      userActiveTimerRef.current = setTimeout(() => setUserActive(false), 3000);
      setKeylogBuffer(prev => {
        const newBuf = [...prev, data.key].slice(-40);
        return newBuf;
      });
      if (keylogTimerRef.current) clearTimeout(keylogTimerRef.current);
      keylogTimerRef.current = setTimeout(() => setKeylogBuffer([]), 4000);
    });

    socket.on('response', (data) => {
      if (data.type === 'fs:list') {
        if (data.requestId !== browseReqIdRef.current) return;
        setBrowseLoading(false);
        if (data.error) { setBrowseError(data.error); return; }
        setBrowsePath(data.path); setBrowseParent(data.parent); setBrowseEntries(data.entries || []);
        return;
      }
      if (data.type === 'clipboard:content') {
        if (data.text) {
          navigator.clipboard.writeText(data.text).then(() => setClipboardStatus('✅ Agent clipboard copied')).catch(() => setClipboardStatus('❌ Could not write to clipboard'));
          setTimeout(() => setClipboardStatus(''), 3000);
        }
        return;
      }
      if (data.type === 'monitor:list') { setMonitors(data.monitors || []); return; }
      if (data.type === 'monitor:switched') { setActiveMonitor(data.index); return; }
      if (data.type === 'file:received') { setTransferStatus(`✅ File saved to agent Desktop: ${data.fileName}`, true); }
      else if (data.type === 'file:error') { setTransferStatus(`❌ Transfer failed: ${data.error}`, true); }
      else if (data.type === 'file:chunk') {
        const key = data.fileName;
        if (!fileChunksRef.current[key]) fileChunksRef.current[key] = { chunks: new Array(data.totalChunks), received: 0, total: data.totalChunks, size: data.fileSize };
        const entry = fileChunksRef.current[key];
        entry.chunks[data.chunkIndex] = data.data; entry.received += 1;
        const pct = Math.round((entry.received / entry.total) * 100);
        const sizeMb = (entry.size / (1024*1024)).toFixed(1);
        setTransferStatus(`⬇️ Downloading ${data.fileName} — ${pct}% of ${sizeMb} MB`);
        if (entry.received === entry.total) {
          try {
            const byteArrays = entry.chunks.map(b64 => { const bin = atob(b64); const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i); return bytes; });
            const total = byteArrays.reduce((s, a) => s + a.length, 0);
            const merged = new Uint8Array(total); let offset = 0;
            for (const arr of byteArrays) { merged.set(arr, offset); offset += arr.length; }
            const url = URL.createObjectURL(new Blob([merged]));
            const a = document.createElement('a'); a.href = url; a.download = data.fileName; a.click();
            URL.revokeObjectURL(url); delete fileChunksRef.current[key];
            setTransferStatus(`✅ Downloaded: ${data.fileName} (${sizeMb} MB)`, true);
          } catch (err) { setTransferStatus(`❌ Failed: ${err.message}`, true); }
        }
      }
    });
  };

  const connectAgent = async (agent) => {
    setActiveAgent(agent); activeAgentRef.current = agent;
    setScreen('control'); setStatus('Connecting...');
    setLoadingScreen(true); setLoadingMsg('Establishing connection...');
    setPrivacyOn(false); setScreenStatus('on'); setScreenAlert(false);
    setAgentLocation(null); setTransferStatus(''); setShowFilePanel(false);
    setBrowsing(false); setBrowseEntries([]);
    setAgentOffline(false); setStreamError(''); setReconnectCountdown(0); agentOfflineRef.current = false;
    setShowChat(false); setChatMsg(''); setChatSent(''); setClipboardStatus('');
    setMonitors([]); setActiveMonitor(1); setShowMonitors(false);
    fetchAgentLocation(agent);
    setTimeout(() => startWebRTC(agent), 800);
    setTimeout(() => sendCommand({ type: 'session:start' }), 1500);
    setTimeout(() => sendCommand({ type: 'monitor:list' }), 2500);
  };

  const startWebRTC = async (agent) => {
    const gen = ++pcGenRef.current;
    if (pcRef.current) { try { pcRef.current.close(); } catch(e){} pcRef.current = null; }
    if (videoRef.current) { videoRef.current.srcObject = null; }
    await new Promise(r => setTimeout(r, 800));
    if (gen !== pcGenRef.current) return;

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    if (latencyIntervalRef.current) clearInterval(latencyIntervalRef.current);
    latencyIntervalRef.current = setInterval(async () => {
      if (pcRef.current) {
        try {
          const stats = await pcRef.current.getStats();
          stats.forEach(report => {
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              const rtt = report.currentRoundTripTime;
              if (rtt !== undefined) {
                const ms = Math.round(rtt * 1000);
                setLatency(ms);
                if (ms < 50) setQuality('excellent');
                else if (ms < 100) setQuality('good');
                else if (ms < 200) setQuality('fair');
                else setQuality('poor');
              }
            }
          });
        } catch {}
      }
    }, 2000);

    pc.ontrack = (event) => {
      if (gen !== pcGenRef.current) return;
      if (event.track.kind !== 'video') return;
      const stream = event.streams[0] || new MediaStream([event.track]);
      setStatus('connected'); setStreamError(''); setAgentOffline(false); agentOfflineRef.current = false;
      setLoadingScreen(false);
      const attach = () => {
        if (!videoRef.current) { setTimeout(attach, 100); return; }
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.disablePictureInPicture = true;
        videoRef.current.play().then(() => setStatus('connected')).catch(() => {
          setTimeout(() => {
            if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.muted = true; videoRef.current.play().catch(() => {}); }
          }, 500);
        });
      };
      attach();
    };

    pc.onicecandidate = (event) => {
      if (gen !== pcGenRef.current || !event.candidate) return;
      socketRef.current.emit('webrtc:ice', { candidate: event.candidate, deviceId: agent.deviceId, target: 'agent', gen });
    };

    pc.oniceconnectionstatechange = () => {
      if (gen !== pcGenRef.current) return;
      const s = pc.iceConnectionState;
      if (s === 'checking') { setStatus('ICE checking...'); setLoadingMsg('Finding best route...'); }
      else if (s === 'failed') { setStatus('Connection failed'); setStreamError('Could not establish stream — retrying in 8s'); setLoadingScreen(false); scheduleReconnect(agent, 8); }
      else if (s === 'disconnected') { setStatus('Stream interrupted'); setStreamError('Stream was interrupted — retrying in 8s'); setLoadingScreen(false); scheduleReconnect(agent, 8); }
    };

    pc.onconnectionstatechange = () => {
      if (gen !== pcGenRef.current) return;
      if (pc.connectionState === 'failed') { setStatus('Connection failed'); setStreamError('Could not reach the remote machine — retrying in 8s'); setLoadingScreen(false); scheduleReconnect(agent, 8); }
    };

    pc.addTransceiver('video', { direction: 'recvonly' });

    const offer = await pc.createOffer();
    const sdp = offer.sdp.replace(/b=AS:\d+/g, 'b=AS:8000').replace(/b=TIAS:\d+/g, 'b=TIAS:8000000');
    await pc.setLocalDescription({ type: offer.type, sdp });
    socketRef.current.emit('webrtc:offer', { offer: { sdp: pc.localDescription.sdp, type: pc.localDescription.type }, deviceId: agent.deviceId, gen });
  };

  const sendCommand = (command) => {
    const agent = activeAgentRef.current;
    if (!agent) return;
    socketRef.current.emit('controller:command', { deviceId: agent.deviceId, command, token: tokenRef.current });
  };

  const deleteAgent = (agent) => {
    if (!window.confirm(`Remove ${agent.name} from dashboard?\n\nThis only removes it from view. The agent will reconnect automatically if still running.`)) return;
    setAgents(prev => prev.filter(a => a.deviceId !== agent.deviceId));
  };

  const uninstallAgent = (agent) => {
    if (!window.confirm(`⚠️ UNINSTALL BRIDGELINK from ${agent.name}?\n\nThis will permanently remove BridgeLink from that machine. It will stop running and disappear from your dashboard.\n\nThis cannot be undone.`)) return;
    // Emit directly via socketRef with specific deviceId — avoids activeAgentRef race condition
    if (socketRef.current?.connected) {
      socketRef.current.emit('controller:command', {
        deviceId: agent.deviceId,
        command: { type: 'agent:uninstall' },
        token: tokenRef.current
      });
    }
    // Wait 2s before removing from view so command reaches agent first
    setTimeout(() => {
      setAgents(prev => prev.filter(a => a.deviceId !== agent.deviceId));
    }, 2000);
    setUninstallToast(`✅ Uninstall command sent to ${agent.name}`);
    setTimeout(() => setUninstallToast(''), 4000);
  };

  const runToolboxScript = (agent, script) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('controller:command', {
      deviceId: agent.deviceId,
      command: { type: 'shell', command: script.command },
      token: tokenRef.current
    });
    setShowToolbox(null);
    setUninstallToast(`✅ "${script.name}" sent to ${agent.name}`);
    setTimeout(() => setUninstallToast(''), 3000);
  };

  const toggleMouse = () => { const next = !mouseEnabled; setMouseEnabled(next); mouseEnabledRef.current = next; };

  const togglePrivacy = () => {
    if (privacyOn) { sendCommand({ type: 'privacy:off' }); setPrivacyOn(false); setScreenStatus('on'); }
    else { sendCommand({ type: 'privacy:on' }); setPrivacyOn(true); setScreenStatus('off'); }
  };

  const showKey = (key) => {
    setLastKey(key);
    if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
    keyTimeoutRef.current = setTimeout(() => setLastKey(''), 1000);
  };

  const getRemoteCoords = (e) => {
    const video = videoRef.current;
    const rect = video.getBoundingClientRect();
    const agentW = activeAgentRef.current?.screenWidth || 1920;
    const agentH = activeAgentRef.current?.screenHeight || 1080;
    const videoAspect = agentW / agentH, elemAspect = rect.width / rect.height;
    let renderW, renderH, offsetX, offsetY;
    if (elemAspect > videoAspect) { renderH = rect.height; renderW = rect.height * videoAspect; offsetX = (rect.width - renderW) / 2; offsetY = 0; }
    else { renderW = rect.width; renderH = rect.width / videoAspect; offsetX = 0; offsetY = (rect.height - renderH) / 2; }
    const x = Math.round((e.clientX - rect.left - offsetX) * agentW / renderW);
    const y = Math.round((e.clientY - rect.top - offsetY) * agentH / renderH);
    return { x: Math.max(0, Math.min(agentW, x)), y: Math.max(0, Math.min(agentH, y)) };
  };

  const handleVideoClick = (e) => {
    if (controlRef.current) controlRef.current.focus();
    if (!mouseEnabledRef.current) return;
    const { x, y } = getRemoteCoords(e);
    const now = Date.now();
    const dist = Math.abs(x - lastClickPos.current.x) + Math.abs(y - lastClickPos.current.y);
    if (now - lastClickTime.current < 300 && dist < 10) {
      sendCommand({ type: 'mouse_dblclick', x, y, button: 'left' });
      lastClickTime.current = 0;
    } else {
      sendCommand({ type: 'mouse_click', x, y, button: 'left' });
      lastClickTime.current = now;
      lastClickPos.current = { x, y };
    }
  };

  const handleVideoRightClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = getRemoteCoords(e);
    sendCommand({ type: 'mouse_click', x, y, button: 'right' });
    if (controlRef.current) controlRef.current.focus();
  };

  const handleVideoMove = (e) => {
    const now = Date.now();
    setCursorPos({ x: e.clientX, y: e.clientY }); setCursorVisible(true);
    if (!mouseEnabledRef.current) return;
    if (now - lastMouseMove.current < 5) return;
    lastMouseMove.current = now;
    const { x, y } = getRemoteCoords(e);
    sendCommand({ type: 'mouse_move', x, y });
  };

  const handleVideoLeave = () => setCursorVisible(false);

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      lastClickTime.current = Date.now(); lastClickPos.current = { x, y };
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      const now = Date.now();
      const dist = Math.abs(x - lastClickPos.current.x) + Math.abs(y - lastClickPos.current.y);
      if (now - lastClickTime.current < 300 && dist < 15) sendCommand({ type: 'mouse_click', x, y, button: 'left' });
      if (controlRef.current) controlRef.current.focus();
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const now = Date.now();
      if (now - lastMouseMove.current < 16) return;
      lastMouseMove.current = now;
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      sendCommand({ type: 'mouse_move', x, y });
    }
  };

  const handleVideoScroll = (e) => {
    e.preventDefault();
    sendCommand({ type: 'mouse_scroll', dx: 0, dy: e.deltaY > 0 ? -3 : 3 });
  };

  const handleKeyDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
      setCursorType('text');
      if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
      keyTimeoutRef.current = setTimeout(() => { setLastKey(''); setCursorType('default'); }, 1000);
    }
    const keyToSend = e.key === 'OS' ? 'Meta' : e.key;
    sendCommand({ type: 'key_press', key: keyToSend });
    showKey(keyToSend === ' ' ? 'Space' : keyToSend);
  };

  const processFileUpload = async (file) => {
    if (!file) return;
    const sizeMb = (file.size / (1024*1024)).toFixed(1);
    setTransferStatus(`⬆️ Sending ${file.name} (${sizeMb} MB)...`);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1];
      const chunkSize = 65536, totalChunks = Math.ceil(base64.length / chunkSize);
      for (let i = 0; i < totalChunks; i++) {
        sendCommand({ type: 'file:send', fileName: file.name, chunkIndex: i, totalChunks, data: base64.slice(i * chunkSize, (i+1) * chunkSize) });
        await new Promise(r => setTimeout(r, 20));
        setTransferStatus(`⬆️ Sending ${file.name} — ${Math.round(((i+1)/totalChunks)*100)}% of ${sizeMb} MB`);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFilePick = (e) => { const file = e.target.files[0]; if (file) processFileUpload(file); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const file = e.dataTransfer.files[0]; if (file) processFileUpload(file); };
  const downloadFile = (filePath, fileName) => { setTransferStatus(`⬇️ Requesting ${fileName}...`); setFileTransferDone(false); sendCommand({ type: 'file:request', path: filePath }); };

  const sendChat = () => {
    if (!chatMsg.trim()) return;
    sendCommand({ type: 'chat:message', message: chatMsg.trim() });
    setChatSent(`✅ Sent: "${chatMsg.trim()}"`);
    setChatMsg('');
    setTimeout(() => setChatSent(''), 4000);
  };

  const pushClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { sendCommand({ type: 'clipboard:set', text }); setClipboardStatus('✅ Your clipboard sent to agent'); setTimeout(() => setClipboardStatus(''), 3000); }
    } catch { setClipboardStatus('❌ Cannot read clipboard'); setTimeout(() => setClipboardStatus(''), 3000); }
  };

  const pullClipboard = () => { sendCommand({ type: 'clipboard:get' }); setClipboardStatus('⟳ Getting agent clipboard...'); };

  const switchMonitor = (index) => {
    setActiveMonitor(index);
    sendCommand({ type: 'monitor:switch', index });
    setShowMonitors(false);
    setTimeout(() => startWebRTC(activeAgentRef.current), 500);
  };

  const takeScreenshot = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `bridgelink-screenshot-${Date.now()}.png`;
    a.click();
  };

  const sendKeyCombo = (keys) => { keys.forEach(key => sendCommand({ type: 'key_press', key })); };

  const startRecording = () => {
    if (!videoRef.current || !videoRef.current.srcObject) return;
    try {
      const stream = videoRef.current.srcObject;
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8' });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const agentName = activeAgentRef.current?.name || 'session';
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        a.href = url;
        a.download = `bridgelink-${agentName}-${timestamp}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        recordingChunksRef.current = [];
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch(e) {
      console.error('Recording failed:', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setRecordingTime(0);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const goBack = () => {
    stopRecording();
    sendCommand({ type: 'session:end' });
    if (privacyOn) sendCommand({ type: 'privacy:off' });
    if (document.fullscreenElement) document.exitFullscreen();
    pcGenRef.current++;
    if (pcRef.current) { try { pcRef.current.close(); } catch(e){} pcRef.current = null; }
    if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.load(); }
    if (latencyIntervalRef.current) clearInterval(latencyIntervalRef.current);
    if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setLatency(null); setQuality(null); setShowKeypad(false);
    setLastKey(''); setPrivacyOn(false); setScreenStatus('on'); setScreenAlert(false);
    setActiveAgent(null); activeAgentRef.current = null;
    setAgentLocation(null); setCursorVisible(false); setCursorType('default');
    setTransferStatus(''); setFileTransferDone(false); setShowFilePanel(false);
    setBrowsing(false); setBrowseEntries([]);
    setAgentOffline(false); setStreamError(''); setReconnectCountdown(0); agentOfflineRef.current = false;
    setShowChat(false); setChatMsg(''); setChatSent(''); setClipboardStatus('');
    setMonitors([]); setActiveMonitor(1); setShowMonitors(false);
    setMouseEnabled(false); mouseEnabledRef.current = false;
    setLoadingScreen(false); setLoadingMsg('Connecting...');
    setRecording(false); setRecordingTime(0);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setUserActive(false); setKeylogBuffer([]);
    if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
    if (keylogTimerRef.current) clearTimeout(keylogTimerRef.current);
    setScreen('dashboard'); setStatus('');
  };

  const fileIcon = (entry) => {
    if (entry.type === 'folder') return '📁';
    const ext = entry.name.split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)) return '🖼️';
    if (['mp4','mkv','avi','mov','wmv'].includes(ext)) return '🎬';
    if (['mp3','wav','flac','aac','ogg'].includes(ext)) return '🎵';
    if (['zip','rar','7z','tar','gz'].includes(ext)) return '🗜️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx'].includes(ext)) return '📊';
    if (['exe','msi'].includes(ext)) return '⚙️';
    if (['py','js','ts','java','cpp','c','sh','bat'].includes(ext)) return '💻';
    return '📃';
  };

  const isConnected = status === 'connected';

  if (!licenseValid) return (
    <div style={styles.container} className="bl-fade">
      <div style={styles.loginBox}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⬡</div>
          <h1 style={styles.logo}>BridgeLink</h1>
          <p style={{ ...styles.sub, marginTop: 4 }}>License Activation</p>
        </div>
        <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #333, transparent)', margin: '8px 0' }} />
        <p style={{ color: '#555', fontSize: 12, textAlign: 'center', lineHeight: 1.6 }}>Enter your license key to activate BridgeLink on this device.</p>
        {licenseError && <div style={styles.errorBox}><span>⚠</span> {licenseError}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>LICENSE KEY</label>
          <input style={{ ...styles.input, textTransform: 'uppercase', letterSpacing: 2 }} type="text" placeholder="BL-XXXX-XXXX-XXXX" value={licenseKey} onChange={e => setLicenseKey(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && !licenseChecking && validateLicense()} autoFocus />
        </div>
        <button className="bl-btn" style={{ ...styles.btn, marginTop: 4, opacity: licenseChecking ? 0.75 : 1, cursor: licenseChecking ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={validateLicense} disabled={licenseChecking}>
          {licenseChecking ? <><span className="bl-spin">⟳</span> Validating...</> : '→ Activate'}
        </button>
        <p style={{ color: '#333', fontSize: 11, textAlign: 'center' }}>Contact your administrator for a license key.</p>
      </div>
    </div>
  );

  if (screen === 'login') return (
    <div style={styles.container} className="bl-fade">
      <div style={styles.loginBox}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⬡</div>
          <h1 style={styles.logo}>BridgeLink</h1>
          <p style={{ ...styles.sub, marginTop: 4 }}>Secure Remote Access</p>
        </div>
        <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #333, transparent)', margin: '8px 0' }} />
        {error && <div style={styles.errorBox}><span>⚠</span> {error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>PASSPHRASE</label>
          <input style={styles.input} type="password" placeholder="Enter passphrase" value={passphrase} onChange={e => setPassphrase(e.target.value)} onKeyDown={e => e.key === 'Enter' && !connecting && login()} autoFocus />
        </div>
        <button className="bl-btn" style={{ ...styles.btn, marginTop: 4, opacity: connecting ? 0.75 : 1, cursor: connecting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={login} disabled={connecting}>
          {connecting ? <><span className="bl-spin">⟳</span> Connecting...</> : '→ Connect'}
        </button>
      </div>
    </div>
  );

  if (screen === 'dashboard') return (
    <div style={styles.container} className="bl-fade">
      <div style={styles.dashboard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h2 style={{ ...styles.logo, fontSize: 22 }}>⬡ BridgeLink</h2>
            <p style={{ ...styles.sub, marginTop: 4 }}>
              <span style={{ color: agents.length > 0 ? '#4cff91' : '#555' }}>●</span>
              {' '}{agents.length} device{agents.length !== 1 ? 's' : ''} online
            </p>
          </div>
          <button className="bl-icon-btn" style={styles.refreshBtn} onClick={refreshAgents} title="Refresh">
            <span style={{ display: 'inline-block', transition: 'transform 0.5s', transform: refreshing ? 'rotate(180deg)' : 'rotate(0deg)' }}>↻</span>
          </button>
        </div>
        {uninstallToast && <div style={{ background: '#0d1a0d', border: '1px solid #4cff91', borderRadius: 8, padding: '10px 16px', color: '#4cff91', fontSize: 12, marginBottom: 12 }} className="bl-fade">{uninstallToast}</div>}
        <div style={styles.agentList}>
          {agents.length === 0 && (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>⬡</div>
              <div style={{ color: '#444', fontSize: 14 }}>No devices online</div>
              <div style={{ color: '#333', fontSize: 12, marginTop: 4 }}>Waiting for agents to connect...</div>
            </div>
          )}
          {agents.map(agent => (
            <div key={agent.deviceId} className="bl-card" style={styles.agentCard} onClick={() => connectAgent(agent)}>
              <div style={styles.agentInfo}>
                <div style={styles.agentDotWrap}><span className="bl-pulse" style={styles.dot}>●</span></div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={styles.agentName}>{agent.name}</span>
                    <span style={styles.agentOs}>{agent.os}</span>
                    {agent.fallbackMode ? <span style={styles.fallbackBadge}>Terminal</span> : <span style={styles.captureBadge}>● Screen</span>}
                    {agent.monitorCount > 1 && <span style={styles.monitorBadge}>🖥 {agent.monitorCount}</span>}
                  </div>
                  <div style={styles.agentMeta}>
                    {agent.screenWidth && `${agent.screenWidth}×${agent.screenHeight}`}
                    {agent.captureMethod && ` · ${agent.captureMethod}`}
                    {agent.ip && ` · ${agent.ip.replace('::ffff:','')}`}
                    {agentPings[agent.deviceId] && <span style={{ color: agentPings[agent.deviceId] < 100 ? '#4cff91' : agentPings[agent.deviceId] < 200 ? '#ffaa00' : '#ff4444', marginLeft: 4 }}>· 📶 ~{agentPings[agent.deviceId]}ms</span>}
                  </div>
                  <AgentLocation ip={agent.ip} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <button
                    title="Run a toolbox script on this machine"
                    style={{ background: showToolbox === agent.deviceId ? '#0d1a0d' : 'transparent', border: '1px solid #4cff9133', borderRadius: 6, padding: '4px 8px', color: '#4cff91', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                    onClick={e => { e.stopPropagation(); setShowToolbox(showToolbox === agent.deviceId ? null : agent.deviceId); setToolboxAgent(agent); }}>🧰 Tools</button>
                  {showToolbox === agent.deviceId && (
                    <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 4, background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 10, minWidth: 220, maxHeight: 280, overflowY: 'auto', zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
                      <div style={{ padding: '10px 14px', borderBottom: '1px solid #1a1a28', color: '#7c6aff', fontSize: 11, fontWeight: 'bold' }}>🧰 Toolbox — {agent.name}</div>
                      {toolboxScripts.length === 0 ? (
                        <div style={{ padding: '16px', color: '#444', fontSize: 12, textAlign: 'center' }}>No scripts yet — add in Admin panel</div>
                      ) : toolboxScripts.map(script => (
                        <div key={script.id} style={{ padding: '10px 14px', borderBottom: '1px solid #111', cursor: 'pointer' }} onClick={() => runToolboxScript(agent, script)}
                          onMouseEnter={e => e.currentTarget.style.background='#151520'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <div style={{ color: '#e8e8f0', fontSize: 12, fontWeight: 500 }}>{script.name}</div>
                          {script.description && <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>{script.description}</div>}
                          <div style={{ color: '#7c6aff', fontSize: 10, fontFamily: 'monospace', marginTop: 3 }}>{script.command.substring(0,40)}{script.command.length > 40 ? '...' : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  title="Remove from view (agent keeps running)"
                  style={{ background: 'transparent', border: '1px solid #ff444433', borderRadius: 6, padding: '4px 8px', color: '#ff6666', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                  onClick={e => { e.stopPropagation(); deleteAgent(agent); }}>✕ Hide</button>
                <button
                  title="Permanently uninstall BridgeLink from this machine"
                  style={{ background: 'transparent', border: '1px solid #ff222233', borderRadius: 6, padding: '4px 8px', color: '#ff4444', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                  onClick={e => { e.stopPropagation(); uninstallAgent(agent); }}>🗑 Uninstall</button>
                <div style={styles.connectArrow}>
                  <span style={{ fontSize: 11, color: '#7c6aff', marginRight: 4 }}>Connect</span>
                  <span style={{ color: '#7c6aff' }}>→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (screen === 'control') return (
    <div ref={controlRef} style={styles.controlContainer} onKeyDown={handleKeyDown} tabIndex={0} onContextMenu={e => e.preventDefault()}>
      <div style={styles.controlBar}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isConnected && <span className="bl-pulse" style={{ color: '#4cff91', fontSize: 8 }}>●</span>}
            <span style={styles.logo}>⬡ {activeAgent?.name}</span>
          </div>
          {agentLocation && <span style={{ color: '#ffaa00', fontSize: 11 }}>{agentLocation}</span>}
          {activeAgent?.ip && <span style={{ color: '#444', fontSize: 10 }}>{activeAgent.ip.replace('::ffff:', '')}</span>}
        </div>
        <div style={styles.controlBarCenter}>
          <span style={{ ...styles.statusBadge, background: isConnected ? '#0a1f0a' : agentOffline ? '#1f0a0a' : '#1a1a0d', color: isConnected ? '#4cff91' : agentOffline ? '#ff4444' : '#ffaa00', border: `1px solid ${isConnected ? '#4cff9133' : agentOffline ? '#ff444433' : '#ffaa0033'}` }}>
            {agentOffline ? '⚠ Offline' : isConnected ? '● Connected' : status}
          </span>
          <span style={{ ...styles.screenStatusBadge, background: screenStatus === 'on' ? '#0a1f0a' : '#1f0a0a', color: screenStatus === 'on' ? '#4cff91' : '#ff4444', border: `1px solid ${screenStatus === 'on' ? '#4cff9133' : '#ff444433'}` }}>
            {screenStatus === 'on' ? '🖥 On' : '🔲 Off'}
          </span>
          <span style={styles.timerBadge}>⏱ {formatTime(sessionTime)}</span>
          {activeAgent && <span style={styles.screenInfo}>{activeAgent.screenWidth}×{activeAgent.screenHeight}</span>}
          {userActive && (
            <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: '#1a0a00', color: '#ffaa00', border: '1px solid #ffaa0066', animation: 'pulse 1s ease-in-out infinite' }}>
              ⚠️ User Active
            </span>
          )}
          {latency !== null && (
            <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, fontFamily: 'inherit', background: quality === 'excellent' ? '#0a1f0a' : quality === 'good' ? '#0a1a0a' : quality === 'fair' ? '#1a1400' : '#1f0a0a', color: quality === 'excellent' ? '#4cff91' : quality === 'good' ? '#7cff7c' : quality === 'fair' ? '#ffaa00' : '#ff4444', border: `1px solid ${quality === 'excellent' ? '#4cff9133' : quality === 'good' ? '#7cff7c33' : quality === 'fair' ? '#ffaa0033' : '#ff444433'}` }}>
              📶 {latency}ms
            </span>
          )}
        </div>
        <div style={styles.controlBarRight}>
          {monitors.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button className="bl-icon-btn" style={styles.monitorBtn} onClick={() => setShowMonitors(!showMonitors)}>🖥 {activeMonitor}</button>
              {showMonitors && (
                <div style={styles.monitorDropdown} className="bl-fade">
                  {monitors.map(m => (
                    <div key={m.index} className="bl-monitor-opt" style={{ ...styles.monitorOption, background: activeMonitor === m.index ? '#1a2a1a' : 'transparent', color: activeMonitor === m.index ? '#4cff91' : '#aaa' }} onClick={() => switchMonitor(m.index)}>
                      {activeMonitor === m.index ? '● ' : '  '}Monitor {m.index} — {m.width}×{m.height}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: mouseEnabled ? '#0d1a0d' : '#2a0808', color: mouseEnabled ? '#4cff91' : '#ff4444', border: mouseEnabled ? '1px solid #4cff9166' : '1px solid #ff444466' }} onClick={toggleMouse} title={mouseEnabled ? 'Mouse control ON — click to disable' : 'Mouse control OFF — click to enable'}>
            🖱 {mouseEnabled ? 'ON' : 'OFF'}
          </button>
          <button className="bl-icon-btn" style={styles.fullscreenBtn} onClick={takeScreenshot} title="Screenshot">📸</button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: recording ? '#2a0808' : '#1a1a2a', color: recording ? '#ff4444' : '#888', border: recording ? '1px solid #ff444466' : '1px solid #2a2a3a', minWidth: 60 }} onClick={toggleRecording} title={recording ? 'Stop recording' : 'Start recording'}>
            {recording ? <>🔴 {Math.floor(recordingTime/60).toString().padStart(2,'0')}:{(recordingTime%60).toString().padStart(2,'0')}</> : '⏺ REC'}
          </button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: showKeypad ? '#1a0a2a' : '#1a1a2a', color: showKeypad ? '#cc88ff' : '#888', border: showKeypad ? '1px solid #cc88ff66' : '1px solid #2a2a3a' }} onClick={() => setShowKeypad(!showKeypad)} title="Key combos">⌨️</button>
          <button className="bl-icon-btn" style={styles.fullscreenBtn} onClick={toggleFullscreen}>{isFullscreen ? '⛶ Exit' : '⛶ Full'}</button>
          <button className="bl-icon-btn" style={{ ...styles.privacyBtn, background: privacyOn ? '#2a0808' : '#1a1a2a', color: privacyOn ? '#ff4444' : '#aaa', border: privacyOn ? '1px solid #ff444466' : '1px solid #333' }} onClick={togglePrivacy}>
            {privacyOn ? '🔒 Privacy' : '👁 Privacy'}
          </button>
          <button className="bl-danger-btn" style={styles.backBtn} onClick={goBack}>← Back</button>
        </div>
      </div>

      {screenAlert && (
        <div style={styles.alert} className="bl-slide">
          <span>⚠️ Screen was turned back on at the remote machine</span>
          <button style={styles.alertClose} onClick={() => setScreenAlert(false)}>✕</button>
        </div>
      )}

      {agentOffline && (
        <div style={styles.offlineOverlay}>
          <div style={styles.offlineBox} className="bl-fade">
            <div style={{ fontSize: 48, marginBottom: 16 }}>📵</div>
            <div style={styles.offlineTitle}>Agent went offline</div>
            <div style={styles.offlineSub}>Waiting for <strong style={{ color: '#aaa' }}>{activeAgent?.name}</strong> to come back online...</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffaa00', fontSize: 13 }}><span className="bl-spin">⟳</span> Monitoring connection...</div>
            <button className="bl-btn" style={{ ...styles.offlineBackBtn, marginTop: 16 }} onClick={goBack}>← Back to dashboard</button>
          </div>
        </div>
      )}

      {!agentOffline && streamError && (
        <div style={styles.streamErrorBanner} className="bl-slide">
          <span className="bl-spin" style={{ flexShrink: 0 }}>⟳</span>
          <span>{streamError}</span>
          {reconnectCountdown > 0 && <span style={{ color: '#ffaa00' }}>Retrying in {reconnectCountdown}s</span>}
          <button className="bl-btn" style={styles.retryNowBtn} onClick={() => { if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current); if (countdownTimerRef.current) clearInterval(countdownTimerRef.current); setReconnectCountdown(0); setStreamError(''); startWebRTC(activeAgentRef.current); }}>Retry now</button>
        </div>
      )}

      {showKeypad && (
        <div style={styles.keypadPanel} className="bl-fade">
          <div style={styles.panelLabel}>KEY COMBOS</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              ['Ctrl+Alt+Del', ['Control','Alt','Delete']],
              ['Win+D', ['Meta','d']],
              ['Alt+F4', ['Alt','F4']],
              ['Win+L', ['Meta','l']],
              ['Alt+Tab', ['Alt','Tab']],
              ['Win Key', ['Meta']],
              ['Ctrl+C', ['Control','c']],
              ['Ctrl+V', ['Control','v']],
              ['Ctrl+Z', ['Control','z']],
              ['Ctrl+A', ['Control','a']],
              ['Ctrl+S', ['Control','s']],
              ['Esc', ['Escape']],
            ].map(([label, keys]) => (
              <button key={label} className="bl-icon-btn" style={styles.keypadBtn} onClick={() => sendKeyCombo(keys)}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {loadingScreen && !agentOffline && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 8999, gap: 20 }}>
          <div style={{ fontSize: 48, animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>⬡</div>
          <div style={{ color: '#7c6aff', fontSize: 16, letterSpacing: 2 }}>{loadingMsg}</div>
          <div style={{ color: '#333', fontSize: 12 }}>Connecting to {activeAgent?.name}...</div>
        </div>
      )}

      <div style={styles.videoWrapper}>
        <video ref={videoRef} autoPlay playsInline muted
          style={{ ...styles.video, opacity: agentOffline ? 0.15 : 1, cursor: 'none' }}
          onClick={handleVideoClick}
          onContextMenu={handleVideoRightClick}
          onMouseMove={handleVideoMove}
          onMouseLeave={handleVideoLeave}
          onWheel={handleVideoScroll}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        />
        {cursorVisible && !agentOffline && (
          <div style={{ position: 'fixed', left: cursorPos.x, top: cursorPos.y, pointerEvents: 'none', zIndex: 9998, transform: 'translate(-2px, -2px)' }}>
            {cursorType === 'text' ? (
              <svg width="14" height="20" viewBox="0 0 14 20" fill="none">
                <line x1="7" y1="0" x2="7" y2="20" stroke="white" strokeWidth="1.5"/>
                <line x1="3" y1="0" x2="11" y2="0" stroke="white" strokeWidth="1.5"/>
                <line x1="3" y1="20" x2="11" y2="20" stroke="white" strokeWidth="1.5"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 2L16 10L10 11L8 18L4 2Z" fill="white" stroke="#222" strokeWidth="1.2"/>
              </svg>
            )}
          </div>
        )}
        {lastKey && <div style={styles.keyIndicator} className="bl-fade"><span style={styles.keyBadge}>{lastKey}</span></div>}
      </div>

      {keylogBuffer.length > 0 && (
        <div style={{ position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 9995, background: 'rgba(10,10,20,0.92)', border: '1px solid #ffaa0044', borderRadius: 10, padding: '8px 16px', maxWidth: 500, backdropFilter: 'blur(4px)' }} className="bl-fade">
          <div style={{ color: '#ffaa00', fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>⌨️ USER TYPING</div>
          <div style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.6 }}>
            {keylogBuffer.map((k, i) => (
              <span key={i} style={{ 
                color: ['Enter','Backspace','Tab','Escape','Delete','Shift','Control','Alt','Meta'].includes(k) ? '#7c6aff' : '#fff',
                background: ['Enter','Backspace','Tab','Escape','Delete','Shift','Control','Alt','Meta'].includes(k) ? '#1a1a2a' : 'transparent',
                borderRadius: 3, padding: ['Enter','Backspace','Tab','Escape','Delete','Shift','Control','Alt','Meta'].includes(k) ? '0 4px' : '0',
                marginRight: 1, fontSize: ['Enter','Backspace','Tab','Escape','Delete','Shift','Control','Alt','Meta'].includes(k) ? 10 : 13
              }}>
                {['Enter','Backspace','Tab','Escape','Delete'].includes(k) ? `[${k}]` : 
                 ['Shift','Control','Alt','Meta'].includes(k) ? '' : k}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={styles.bottomToolbar}>
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: showChat ? '#0d1f0d' : '#1a1a2a', borderColor: showChat ? '#4cff9166' : '#2a2a3a', color: showChat ? '#4cff91' : '#7c6aff' }} onClick={() => { setShowChat(!showChat); setShowFilePanel(false); }}>💬 Chat</button>
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: showFilePanel ? '#0d1f0d' : '#1a1a2a', borderColor: showFilePanel ? '#4cff9166' : '#2a2a3a', color: showFilePanel ? '#4cff91' : '#7c6aff' }} onClick={() => { setShowFilePanel(!showFilePanel); setShowChat(false); }}>📁 Files</button>
      </div>

      {showChat && (
        <div style={styles.chatBox} className="bl-fade">
          <div style={styles.panelLabel}>MESSAGE TO AGENT</div>
          <div style={styles.chatInputRow}>
            <input style={styles.chatInput} type="text" placeholder="Type a message..." value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') sendChat(); }} />
            <button className="bl-btn" style={styles.chatSendBtn} onClick={sendChat}>Send</button>
          </div>
          {chatSent && <div style={{ color: '#4cff91', fontSize: 11 }} className="bl-fade">{chatSent}</div>}
          <div style={{ height: 1, background: '#1a1a2a', margin: '2px 0' }} />
          <div style={styles.panelLabel}>CLIPBOARD</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="bl-icon-btn" style={styles.clipBtn} onClick={pushClipboard}>⬆ Push mine</button>
            <button className="bl-icon-btn" style={styles.clipBtn} onClick={pullClipboard}>⬇ Get agent's</button>
          </div>
          {clipboardStatus && <div style={{ color: clipboardStatus.startsWith('✅') ? '#4cff91' : '#ff4444', fontSize: 11 }} className="bl-fade">{clipboardStatus}</div>}
        </div>
      )}

      <div style={styles.filePanel}>
        {showFilePanel && (
          <div style={styles.filePanelContent} className="bl-fade">
            <div style={styles.panelLabel}>SEND TO AGENT DESKTOP</div>
            <div style={{ ...styles.dropZone, borderColor: dragOver ? '#7c6aff' : '#2a2a3a', background: dragOver ? '#13131f' : '#0a0a14' }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById('bl-file-input').click()}>
              <span style={{ fontSize: 24, opacity: 0.6 }}>📂</span>
              <span style={{ color: '#444', fontSize: 12 }}>Drag & drop or click to pick</span>
              <input id="bl-file-input" type="file" style={{ display: 'none' }} onChange={handleFilePick} />
            </div>
            <div style={{ height: 1, background: '#1a1a2a', margin: '4px 0' }} />
            <div style={styles.panelLabel}>GET FROM AGENT</div>
            {!browsing ? (
              <button className="bl-icon-btn" style={styles.fileBtn} onClick={openBrowser}>📂 Browse Files</button>
            ) : (
              <div style={styles.fileBrowser}>
                <div style={styles.fileBrowserBar}>
                  <button className="bl-icon-btn" style={styles.fileBrowserBackBtn} onClick={() => browseParent ? browseDir(browseParent) : null} disabled={!browseParent}>← Up</button>
                  <span style={styles.fileBrowserPath}>{browsePath}</span>
                  <button style={styles.fileBrowserCloseBtn} onClick={() => setBrowsing(false)}>✕</button>
                </div>
                {browseLoading && <div style={styles.fileBrowserLoading}><span className="bl-spin">⟳</span> Loading...</div>}
                {browseError && <div style={styles.fileBrowserError}>❌ {browseError}</div>}
                {!browseLoading && !browseError && (
                  <div style={styles.fileBrowserList}>
                    {browseEntries.length === 0 && <div style={styles.fileBrowserEmpty}>Empty folder</div>}
                    {browseEntries.map((entry, i) => (
                      <div key={i} className="bl-file-entry" style={{ ...styles.fileBrowserEntry, cursor: entry.type === 'folder' ? 'pointer' : 'default' }} onClick={() => entry.type === 'folder' ? browseDir(entry.path) : null}>
                        <span style={styles.fileBrowserIcon}>{fileIcon(entry)}</span>
                        <span style={styles.fileBrowserName}>{entry.name}</span>
                        {entry.type === 'file' && (<><span style={styles.fileBrowserSize}>{entry.sizeStr}</span><button className="bl-btn" style={styles.fileBrowserDl} onClick={(e) => { e.stopPropagation(); downloadFile(entry.path, entry.name); }}>⬇</button></>)}
                        {entry.type === 'folder' && <span style={styles.fileBrowserArrow}>›</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fileTransferStatus && <div style={{ ...styles.fileStatus, color: fileTransferDone ? (fileTransferStatus.startsWith('✅') ? '#4cff91' : '#ff4444') : '#ffaa00' }} className="bl-fade">{fileTransferStatus}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#07070e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'SF Mono', 'Fira Code', monospace" },
  loginBox: { background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 16, padding: 40, width: 360, display: 'flex', flexDirection: 'column', gap: 18, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  logo: { color: '#8b7fff', margin: 0, fontSize: 24, letterSpacing: 3, fontWeight: 400 },
  sub: { color: '#444', margin: 0, fontSize: 13 },
  errorBox: { background: '#1a0808', border: '1px solid #ff444433', borderRadius: 8, padding: '10px 14px', color: '#ff6666', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  input: { background: '#0a0a14', border: '1px solid #252535', borderRadius: 8, padding: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'inherit', width: '100%' },
  btn: { background: 'linear-gradient(135deg, #7c6aff, #9b8aff)', border: 'none', borderRadius: 8, padding: '12px 16px', color: '#fff', fontSize: 14, cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' },
  dashboard: { width: 560, fontFamily: 'inherit' },
  refreshBtn: { background: '#1a1a2a', border: '1px solid #2a2a3a', borderRadius: 8, width: 38, height: 38, color: '#7c6aff', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  agentList: { display: 'flex', flexDirection: 'column', gap: 10 },
  agentCard: { background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  agentInfo: { display: 'flex', alignItems: 'center', gap: 12, flex: 1 },
  agentDotWrap: { width: 8, flexShrink: 0 },
  dot: { color: '#4cff91', fontSize: 8 },
  agentName: { color: '#e8e8f0', fontSize: 15, fontWeight: 500 },
  agentOs: { color: '#444', fontSize: 11, background: '#151520', padding: '2px 8px', borderRadius: 6 },
  connectArrow: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingLeft: 8 },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', textAlign: 'center' },
  controlContainer: { height: '100dvh', maxHeight: '100dvh', background: '#000', display: 'flex', flexDirection: 'column', outline: 'none', fontFamily: 'inherit', overflow: 'hidden' },
  controlBar: { background: '#0c0c14', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1a1a28', flexWrap: 'wrap', gap: 8 },
  controlBarCenter: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  controlBarRight: { display: 'flex', alignItems: 'center', gap: 6 },
  statusBadge: { fontSize: 11, padding: '4px 10px', borderRadius: 20, fontFamily: 'inherit' },
  screenStatusBadge: { fontSize: 11, padding: '4px 10px', borderRadius: 20, fontFamily: 'inherit' },
  timerBadge: { color: '#7c6aff', fontSize: 11, background: '#0d0a1f', padding: '4px 10px', borderRadius: 20, border: '1px solid #7c6aff33' },
  screenInfo: { color: '#333', fontSize: 11 },
  fullscreenBtn: { background: '#1a1a2a', border: '1px solid #2a2a3a', borderRadius: 6, padding: '6px 10px', color: '#888', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' },
  privacyBtn: { borderRadius: 6, padding: '6px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  backBtn: { background: 'transparent', border: '1px solid #2a2a2a', borderRadius: 6, padding: '6px 12px', color: '#666', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 },
  monitorBtn: { background: '#1a1a2a', border: '1px solid #2a2a3a', borderRadius: 6, padding: '6px 10px', color: '#888', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' },
  monitorDropdown: { position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#0f0f18', border: '1px solid #252535', borderRadius: 10, overflow: 'hidden', zIndex: 9999, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  monitorOption: { padding: '10px 16px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  alert: { background: '#1a0808', borderLeft: '3px solid #ff4444', color: '#ff8888', padding: '10px 20px', fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  alertClose: { background: 'transparent', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 16 },
  videoWrapper: { flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minHeight: 0, minWidth: 0, background: '#000', width: '100%' },
  video: { width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, background: '#000', transition: 'opacity 0.4s', objectFit: 'contain' },
  keyIndicator: { position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 9999 },
  keyBadge: { background: 'rgba(124, 106, 255, 0.9)', color: '#fff', padding: '8px 22px', borderRadius: 8, fontSize: 15, fontFamily: 'inherit', backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(124,106,255,0.3)' },
  fallbackBadge: { background: '#1a1000', color: '#ffaa00', fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid #ffaa0033' },
  captureBadge: { background: '#001a00', color: '#4cff91', fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid #4cff9133' },
  monitorBadge: { background: '#0a0a1f', color: '#7c6aff', fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid #7c6aff33' },
  agentMeta: { color: '#333', fontSize: 11, marginTop: 4 },
  offlineOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, backdropFilter: 'blur(4px)' },
  offlineBox: { background: '#0f0f18', border: '1px solid #ff444433', borderRadius: 20, padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, maxWidth: 380, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
  offlineTitle: { color: '#ff6666', fontSize: 20, fontWeight: 500 },
  offlineSub: { color: '#444', fontSize: 13, lineHeight: 1.6 },
  offlineBackBtn: { background: 'transparent', border: '1px solid #252535', borderRadius: 8, padding: '10px 24px', color: '#666', cursor: 'pointer', fontFamily: 'inherit' },
  streamErrorBanner: { background: '#0f0808', borderLeft: '3px solid #ff4444', color: '#ff8888', padding: '10px 20px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit' },
  retryNowBtn: { background: '#1a0808', border: '1px solid #ff444466', borderRadius: 6, padding: '4px 12px', color: '#ff6666', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, marginLeft: 'auto' },
  bottomToolbar: { position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9997, display: 'flex', gap: 8, background: '#0c0c14', border: '1px solid #1a1a28', borderRadius: 12, padding: '6px 8px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' },
  toolbarBtn: { border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
  chatBox: { position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)', background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 320, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9996 },
  panelLabel: { color: '#333', fontSize: 10, letterSpacing: 1.5 },
  chatInputRow: { display: 'flex', gap: 8 },
  chatInput: { flex: 1, background: '#0a0a14', border: '1px solid #252535', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 12, fontFamily: 'inherit', outline: 'none' },
  chatSendBtn: { background: '#7c6aff', border: 'none', borderRadius: 8, padding: '8px 14px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  clipBtn: { flex: 1, background: '#151520', border: '1px solid #252535', borderRadius: 8, padding: '7px 10px', color: '#888', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, textAlign: 'center' },
  filePanel: { position: 'fixed', bottom: 70, right: 20, zIndex: 9996 },
  filePanelContent: { background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 12, padding: 16, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 340, maxWidth: 400, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  dropZone: { border: '1px dashed #2a2a3a', borderRadius: 10, padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', transition: 'all 0.2s' },
  fileBtn: { background: '#151520', border: '1px solid #7c6aff33', borderRadius: 8, padding: '8px 14px', color: '#7c6aff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  fileStatus: { fontSize: 11, fontFamily: 'inherit', padding: '8px 10px', background: '#0a0a14', borderRadius: 8, wordBreak: 'break-all', border: '1px solid #1a1a2a' },
  fileBrowser: { background: '#0a0a14', border: '1px solid #1e1e2e', borderRadius: 10, overflow: 'hidden' },
  fileBrowserBar: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid #1a1a28', background: '#0f0f1a' },
  fileBrowserBackBtn: { background: '#151520', border: '1px solid #252535', borderRadius: 6, padding: '3px 8px', color: '#888', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 },
  fileBrowserPath: { flex: 1, color: '#7c6aff', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileBrowserCloseBtn: { background: 'transparent', border: 'none', color: '#444', cursor: 'pointer', fontSize: 14, padding: '0 4px' },
  fileBrowserLoading: { color: '#ffaa00', fontSize: 11, padding: '14px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  fileBrowserError: { color: '#ff4444', fontSize: 11, padding: '12px' },
  fileBrowserEmpty: { color: '#333', fontSize: 11, padding: '14px', textAlign: 'center' },
  fileBrowserList: { maxHeight: 260, overflowY: 'auto' },
  fileBrowserEntry: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid #111' },
  fileBrowserIcon: { fontSize: 13, flexShrink: 0 },
  fileBrowserName: { flex: 1, color: '#ccc', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileBrowserSize: { color: '#333', fontSize: 10, flexShrink: 0 },
  fileBrowserDl: { background: '#0a1a0a', border: '1px solid #4cff9133', borderRadius: 6, padding: '3px 8px', color: '#4cff91', cursor: 'pointer', fontSize: 11, flexShrink: 0 },
  fileBrowserArrow: { color: '#333', fontSize: 16, flexShrink: 0 },
  keypadPanel: { background: '#0f0f18', border: '1px solid #1e1e2e', borderTop: 'none', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 },
  keypadBtn: { background: '#151520', border: '1px solid #252535', borderRadius: 6, padding: '6px 10px', color: '#cc88ff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, whiteSpace: 'nowrap' },
};